import { getDay, isAfter, isBefore, isEqual, set } from 'date-fns'

import { DateInterval, TimeInterval, TimeOfDay, Weekday, WeekTimeIntervals } from './model'

export default function (
  availableTimes: WeekTimeIntervals,
  disablePastDates: boolean
): { isAvailable(slot: DateInterval): boolean; minTime(date: Date): Date; maxTime(date: Date): Date } {
  const getWeekdayConfig = (date: Date) => availableTimes[getDay(date)]

  const minMaxTimeMap = getMinMaxTimeMap(availableTimes)

  const isAvailable = (slot: DateInterval) =>
    (!disablePastDates || isAfter(slot.start, Date.now())) &&
    getWeekdayConfig(slot.start)?.some((interval) => slotIsInsideInterval(slot, interval))
  const minTime = (date: Date) => setTime(date, minMaxTimeMap[getDay(date)].min)
  const maxTime = (date: Date) => setTime(date, minMaxTimeMap[getDay(date)].max)

  return { isAvailable, minTime, maxTime }
}

const slotIsInsideInterval = (slot: DateInterval, interval: TimeInterval) =>
  slotIsBetween(slot, timeIntervalInDay(slot.start, interval))

const timeIntervalInDay = (date: Date, interval: TimeInterval) => ({
  start: setTime(date, interval.start),
  end: setTime(date, interval.end),
})

const slotIsBetween = (slot: DateInterval, interval: DateInterval) =>
  isBetween(slot.start, interval) && isBetween(slot.end, interval)

const isBetween = (date: Date, interval: DateInterval) =>
  (isEqual(date, interval.end) || isBefore(date, interval.end)) &&
  (isEqual(date, interval.start) || isAfter(date, interval.start))

const getMinMaxTimeMap = (availableTimes: WeekTimeIntervals): Record<Weekday, { min: TimeOfDay; max: TimeOfDay }> =>
  Object.values(Weekday).reduce((acc, curr: Weekday) => {
    acc[curr] = getMinMax(availableTimes, curr)
    return acc
  }, {} as Record<Weekday, { min: TimeOfDay; max: TimeOfDay }>)

const getMinMax = (availableTimes: WeekTimeIntervals, weekday: Weekday) => {
  const weekdayConfig = availableTimes[weekday]
  if (!weekdayConfig) return { max: { hours: 23, minutes: 59 }, min: { hours: 0, minutes: 0 } }

  const result = { min: { hours: 24, minutes: 60 }, max: { hours: 0, minutes: 0 } }

  for (let { start, end } of weekdayConfig) {
    if (start.hours + start.minutes / 100 < result.min.hours + result.min.minutes / 100) result.min = start
    if (end.hours + end.minutes / 100 > result.max.hours + result.max.minutes / 100) result.max = end
  }

  return result
}

const setTime = (date: Date, time: TimeOfDay) =>
  set(date, { ...(time ?? { hours: 0, minutes: 0 }), seconds: 0, milliseconds: 0 })
